import { useEffect, useRef, useState } from "react";
import StudyText from "@/components/StudyText";
import { ChevronDown } from "lucide-react";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const parsingLabel = (p) => p.replace(/^verb,\s*/i, "");

function WordCard({ w, index, focused }) {
  const [open, setOpen] = useState(index === 0);
  const ref = useRef(null);
  const slug = slugify(w.name);

  useEffect(() => {
    if (!focused) return;
    setOpen(true);
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    return () => clearTimeout(t);
  }, [focused]);

  return (
    <div
      ref={ref}
      className={`qg-rise rounded-2xl border bg-card p-6 transition-colors ${focused ? "border-gold ring-2 ring-gold/30" : "border-border"}`}
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`word-card-${slug}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-4 text-left"
        data-testid={`word-toggle-${slug}`}
      >
        <div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-serif text-2xl font-semibold text-foreground">{w.word}</span>
            {w.original && <span className="font-serif text-2xl text-gold">{w.original}</span>}
            {w.transliteration && <span className="italic text-muted-foreground">{w.transliteration}</span>}
          </div>
          <div className="mt-1 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {w.language && <span>{w.language}</span>}
            {w.strongs && <span>· Strong&apos;s {w.strongs}</span>}
            {!w.parsing && w.morph && <span>· {w.morph}</span>}
            {w.pronunciation && <span className="normal-case tracking-normal">· {w.pronunciation}</span>}
          </div>
          {(w.senses?.length > 0 || w.gloss) && (
            <p className="mt-3 text-base font-medium leading-snug text-foreground/90" data-testid={`word-short-${slug}`}>
              {w.senses?.length ? w.senses.join(", ") : w.gloss}
            </p>
          )}
          {(w.parsing || w.verse_gloss) && (
            <p className="mt-2 flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground" data-testid={`word-parsing-${slug}`}>
              {w.parsing && <span className="italic">{parsingLabel(w.parsing)}</span>}
              {w.verse_gloss && <span>· here: &ldquo;{w.verse_gloss}&rdquo;</span>}
            </p>
          )}
        </div>
        <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-5 space-y-6 border-t border-border pt-5">
          {w.definition && (
            <section>
              <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Strong&apos;s definition</h4>
              <StudyText text={w.definition} />
            </section>
          )}
          {w.kjv_usage && (
            <section>
              <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">How the KJV translates it</h4>
              <p className="text-base leading-relaxed text-muted-foreground">{w.kjv_usage}</p>
            </section>
          )}
          {w.root && (
            <section>
              <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Derivation</h4>
              <p className="text-base leading-relaxed text-muted-foreground">{w.root}</p>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function MinorWords({ words, focusName }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const focused = words.some((w) => w.name === focusName);

  useEffect(() => {
    if (!focused) return;
    setOpen(true);
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    return () => clearTimeout(t);
  }, [focused, focusName]);

  if (!words.length) return null;
  return (
    <div ref={ref} className="rounded-2xl border border-dashed border-border bg-card/60 p-6" data-testid="minor-words">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 text-left"
        data-testid="minor-words-toggle"
      >
        <div>
          <h3 className="font-serif text-xl font-semibold text-foreground">Every other word in this verse</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {words.length} connecting word{words.length > 1 ? "s" : ""} — pronouns, particles, conjunctions — each with its Strong&apos;s number.
          </p>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-5 divide-y divide-border border-t border-border">
          {words.map((w) => (
            <li
              key={w.name}
              className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 py-3 ${focusName === w.name ? "rounded-md bg-gold/10 px-2" : ""}`}
              data-testid={`minor-word-${slugify(w.name)}`}
            >
              <span className="font-serif text-lg font-semibold text-foreground">{w.word}</span>
              <span className="font-serif text-lg text-gold">{w.original}</span>
              <span className="italic text-muted-foreground">{w.transliteration}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{w.strongs}</span>
              {w.parsing && <span className="basis-full text-xs italic text-muted-foreground sm:basis-auto">{parsingLabel(w.parsing)}</span>}
              <span className="basis-full text-sm leading-relaxed text-muted-foreground sm:basis-auto sm:flex-1">
                {w.senses?.length ? w.senses.slice(0, 4).join(", ") : w.gloss || (w.definition || "").split(/;|\./)[0]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function WordStudy({ words, focusName }) {
  if (!words?.length) return null;
  const key = words.filter((w) => !w.minor);
  const minor = words.filter((w) => w.minor);
  return (
    <div className="space-y-4" data-testid="word-study">
      {key.map((w, i) => (
        <WordCard key={w.name} w={w} index={i} focused={focusName === w.name} />
      ))}
      <MinorWords words={minor} focusName={focusName} />
      <p className="pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="word-study-credit">
        Leningrad Codex (WLC/UXLC) for the Old Testament and Textus Receptus (Scrivener 1894) for the New Testament · Word tagging and contextual glosses:{" "}
        <a href="https://www.stepbible.org/" target="_blank" rel="noreferrer" className="underline decoration-gold/60 underline-offset-2 hover:text-foreground" data-testid="credit-stepbible-link">STEPBible.org</a>{" "}
        (CC BY 4.0) · Definitions: Strong&apos;s Exhaustive Concordance, Brown-Driver-Briggs (Hebrew), and Abbott-Smith (Greek), public domain · Lexicon data via Open Scriptures (CC BY 4.0)
      </p>
    </div>
  );
}
